import { signInWithCustomToken, getAuth } from 'firebase/auth';

const LOGIN_URL        = import.meta.env.VITE_LOGIN_URL        || 'https://login.rascalrepublic.com';
const OKR_URL          = import.meta.env.VITE_OKR_URL          || 'https://okr.rascalrepublic.com';
const EPR_URL          = import.meta.env.VITE_EPR_URL          || 'https://epr.rascalrepublic.com';
const PORTAL_URL       = import.meta.env.VITE_PORTAL_URL       || 'https://portal.rascalrepublic.com';
const SSO_FUNCTION_URL = import.meta.env.VITE_SSO_FUNCTION_URL || '';

export const APP_URLS = { LOGIN_URL, OKR_URL, EPR_URL, PORTAL_URL };

/** On app mount — check URL for ?sso_token= and auto-login. */
export async function handleSSOTokenFromURL(): Promise<boolean> {
  const params   = new URLSearchParams(window.location.search);
  const ssoToken = params.get('sso_token');
  if (!ssoToken) return false;

  try {
    await signInWithCustomToken(getAuth(), ssoToken);
    params.delete('sso_token');
    const cleanURL = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', cleanURL);
    return true;
  } catch (err: any) {
    console.error('SSO token exchange failed:', err.message);
    redirectToLogin();
    return false;
  }
}

/**
 * Redirect to the central login portal.
 * Passes current app URL as ?redirect= so login knows where to send user back.
 * Includes loop detection — if redirected more than twice in 15s, shows error instead.
 */
export function redirectToLogin() {
  const now = Date.now();
  const key = '__sso_redirect_ts';
  const countKey = '__sso_redirect_count';
  const lastTs = Number(sessionStorage.getItem(key) || '0');
  const count = Number(sessionStorage.getItem(countKey) || '0');

  // Reset counter if last redirect was more than 15s ago
  if (now - lastTs > 15_000) {
    sessionStorage.setItem(key, String(now));
    sessionStorage.setItem(countKey, '1');
  } else {
    sessionStorage.setItem(key, String(now));
    sessionStorage.setItem(countKey, String(count + 1));
  }

  if (count >= 2) {
    // Loop detected — stop redirecting
    console.error('🔁 SSO redirect loop detected — stopping.');
    sessionStorage.removeItem(key);
    sessionStorage.removeItem(countKey);
    document.body.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui">
        <div style="text-align:center;max-width:400px;padding:2rem">
          <h2 style="margin-bottom:0.5rem">Authentication Error</h2>
          <p style="color:#666">Your account is authenticated but not yet provisioned for this app. Please contact your administrator.</p>
          <button onclick="sessionStorage.clear();location.href='${LOGIN_URL}'" style="margin-top:1rem;padding:0.5rem 1.5rem;border:1px solid #ccc;border-radius:6px;cursor:pointer;background:#fff">Back to Login</button>
        </div>
      </div>`;
    return;
  }

  const currentUrl = window.location.origin + window.location.pathname;
  window.location.href = `${LOGIN_URL}?redirect=${encodeURIComponent(currentUrl)}`;
}

/** Generate a cross-app SSO link for AppSwitcher navigation. */
export async function generateSSOLink(targetAppURL: string): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) return `${LOGIN_URL}?redirect=${encodeURIComponent(targetAppURL)}`;

  try {
    const idToken = await user.getIdToken(true);
    if (!SSO_FUNCTION_URL) return `${LOGIN_URL}?redirect=${encodeURIComponent(targetAppURL)}`;

    const res = await fetch(SSO_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });
    if (!res.ok) throw new Error(`mintSSOToken ${res.status}`);

    const { customToken } = await res.json();
    return `${targetAppURL}?sso_token=${encodeURIComponent(customToken)}`;
  } catch (err: any) {
    return `${LOGIN_URL}?redirect=${encodeURIComponent(targetAppURL)}`;
  }
}

export async function navigateToApp(targetAppURL: string) {
  window.location.href = await generateSSOLink(targetAppURL);
}
